import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Stack,
  IconButton,
  Tooltip,
  Badge,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RepeatIcon from '@mui/icons-material/Repeat';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { taskPropType } from './kanbanTaskConstants';

export default function KanbanTaskCard({
  task,
  isFirstColumn,
  isLastColumn,
  onEdit,
  onDelete,
  onOpenNotes,
  onAdvance,
  onPrevious,
}) {
  const overdue =
    task.executionDate !== null &&
    task.columnId !== 'DONE' &&
    task.executionDate.isBefore(dayjs(), 'day');

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        flexShrink: 0,
        '&:hover': { boxShadow: 2 },
      }}
    >
      <CardContent sx={{ p: 1.5, pb: 0.5 }}>
        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{task.title}</Typography>
        {task.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
            {task.description}
          </Typography>
        )}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {task.executionDate !== null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: overdue ? 'error.main' : 'text.secondary',
              }}
            >
              <EventIcon sx={{ fontSize: 14 }} />
              <Typography variant="body2" sx={{ fontWeight: overdue ? 600 : 400 }}>
                {task.executionDate.format('DD/MM/YYYY')}
              </Typography>
            </Box>
          )}
          {task.time && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'text.secondary',
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 14 }} />
              <Typography variant="body2">{task.time.format('HH:mm')}</Typography>
            </Box>
          )}
          {task.recurringTaskId && (
            <Tooltip title="Tarefa recorrente">
              <RepeatIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </Tooltip>
          )}
        </Stack>
      </CardContent>
      <CardActions sx={{ px: 1, py: 0.5, justifyContent: 'space-between' }}>
        <Box>
          <Tooltip title="Anotações">
            <IconButton size="small" onClick={() => onOpenNotes(task)}>
              <Badge badgeContent={task.notes ? task.notes.length : 0} color="primary">
                <ChatBubbleOutlineIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => onEdit(task)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => onDelete(task.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          {!isFirstColumn && (
            <Tooltip title="Voltar etapa">
              <IconButton size="small" onClick={() => onPrevious(task)}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!isLastColumn && (
            <Tooltip title="Avançar etapa">
              <IconButton size="small" onClick={() => onAdvance(task)}>
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </CardActions>
    </Card>
  );
}

KanbanTaskCard.propTypes = {
  task: taskPropType.isRequired,
  isFirstColumn: PropTypes.bool.isRequired,
  isLastColumn: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onOpenNotes: PropTypes.func.isRequired,
  onAdvance: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
};
